// Types for different droppable areas
export enum DropTargetType {
  EMPLOYEE = 'employee',
  RESOURCE = 'resource',
  UNASSIGNED = 'unassigned',
}

export type EmployeeDropTarget = {
  type: DropTargetType.EMPLOYEE;
  employeeId: string;
  shiftId: string;
};

export type ResourceDropTarget = {
  type: DropTargetType.RESOURCE;
  resourceId: string;
  schedulerConfigId: string;
  resourceIndex: number;
};

export type UnassignedDropTarget = {
  type: DropTargetType.UNASSIGNED;
};

export type DropTarget = EmployeeDropTarget | ResourceDropTarget | UnassignedDropTarget;

// ID prefixes for deterministic parsing
export const RESOURCE_ID_PREFIX = 'resource:';
export const EMPLOYEE_ID_PREFIX = 'employee:';
export const UNASSIGNED_ID = 'unassigned';

// Helper functions to create typed IDs
export const createResourceId = (schedulerConfigId: string, resourceIndex: number): string => {
  return `${RESOURCE_ID_PREFIX}${schedulerConfigId}-${resourceIndex}`;
};

export const createEmployeeId = (employeeId: string): string => {
  return `${EMPLOYEE_ID_PREFIX}${employeeId}`;
};

// Helper functions to identify drop target types
export const parseDropTargetId = (id: string | number): DropTarget | null => {
  if (typeof id !== 'string') {
    return null;
  }

  // Handle unassigned column
  if (id === UNASSIGNED_ID) {
    return {
      type: DropTargetType.UNASSIGNED,
    };
  }

  // Handle resource columns (format: "resource:configId-resourceIndex")
  if (id.startsWith(RESOURCE_ID_PREFIX)) {
    const resourcePart = id.slice(RESOURCE_ID_PREFIX.length);
    const parts = resourcePart.split('-');
    if (parts.length === 2) {
      const [configId, resourceIndex] = parts;
      const parsedIndex = parseInt(resourceIndex, 10);
      if (!Number.isNaN(parsedIndex)) {
        return {
          type: DropTargetType.RESOURCE,
          resourceId: id,
          schedulerConfigId: configId,
          resourceIndex: parsedIndex,
        };
      }
    }
    return null; // Invalid resource ID format
  }

  // Handle employee IDs (format: "employee:employeeId")
  if (id.startsWith(EMPLOYEE_ID_PREFIX)) {
    const employeeId = id.slice(EMPLOYEE_ID_PREFIX.length);
    return {
      type: DropTargetType.EMPLOYEE,
      employeeId,
      shiftId: id,
    };
  }

  // For backward compatibility, treat unprefixed IDs as employee IDs
  return {
    type: DropTargetType.EMPLOYEE,
    employeeId: id,
    shiftId: id,
  };
};

export const isResourceDropTarget = (target: DropTarget): target is ResourceDropTarget => {
  return target.type === DropTargetType.RESOURCE;
};

export const isEmployeeDropTarget = (target: DropTarget): target is EmployeeDropTarget => {
  return target.type === DropTargetType.EMPLOYEE;
};

export const isUnassignedDropTarget = (target: DropTarget): target is UnassignedDropTarget => {
  return target.type === DropTargetType.UNASSIGNED;
};
